import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Divider,
    Autocomplete,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { shopModel } from '../Axios/AxiosConfig';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

// Interfaces mapping to the backend expected payload
interface CartItem {
    id: string; // Used for order item id
    sku?: string;
    name: string;
    productName?: string;
    companyid: string;
    company?: string;
    price: number;
    actualprice?: number;
    quantity: number;
    src?: string; // Image URL
    image?: string; // Image URL
    actualQty?: number;
    imgurl?: string;
}

interface CustomerDetails {
    billing: {
        contact: string;
        email: string;
        name: string;
    };
    shipping: {
        addressLine1: string;
        city: string;
        state: string;
        postalCode: string;
        name: string;
        phone: string;
    };
}

const CreateOfflineOrderPage: React.FC = () => {
    const navigate = useNavigate();

    // State
    const [loading, setLoading] = useState<boolean>(false);
    const [testKitsLoading, setTestKitsLoading] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [testKits, setTestKits] = useState<any[]>([]);
    const [orderDate, setOrderDate] = useState<Date | null>(new Date());

    // Available Products (Search)
    const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [autocompleteInputValue, setAutocompleteInputValue] = useState<string>('');

    // Form State
    const [cart, setCart] = useState<CartItem[]>([]);
    const [discount, setDiscount] = useState<string>("0");

    const [customer, setCustomer] = useState<CustomerDetails>({
        billing: { contact: '', email: '', name: '' },
        shipping: { addressLine1: '', city: '', state: '', postalCode: '', name: '', phone: '' },
    });

    // Quick copy checkbox state to make shipping same as billing name/contact
    const [useBillingForShipping, setUseBillingForShipping] = useState<boolean>(true);


    // Pre-load test kits on mount
    const fetchTestKits = async () => {
        try {
            setTestKitsLoading(true);
            const response = await shopModel.get('/getAllTestkit', {});
            const kits = response.data?.data || response.data || [];
            setTestKits(Array.isArray(kits) ? kits : []);
        } catch (err) {
            console.error('Failed to load test kits', err);
            toast.error('Failed to load test kits');
        } finally {
            setTestKitsLoading(false);
        }
    };

    useEffect(() => {
        fetchTestKits();
    }, []);

    const fetchSearchSuggestions = async (query: string) => {
        if (!query.trim()) {
            setSearchSuggestions([]);
            return;
        }

        try {
            setLoading(true);
            const response = await shopModel.post('/SearchProducts', { search: query.trim() });
            const data: any[] = Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : [];
            setSearchSuggestions(data);
        } catch (err) {
            console.error('Error fetching search suggestions:', err);
            setSearchSuggestions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm) {
                fetchSearchSuggestions(searchTerm);
            } else {
                setSearchSuggestions([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    // Handlers
    const handleCustomerChange = (section: 'billing' | 'shipping', field: string, value: string) => {
        setCustomer(prev => {
            const updated = {
                ...prev,
                [section]: { ...prev[section], [field]: value }
            };

            if (useBillingForShipping && section === 'billing') {
                if (field === 'name') updated.shipping.name = value;
                if (field === 'contact') updated.shipping.phone = value;
            }
            return updated;
        });
    };

    const handleAddProduct = (product: any) => {
        if (!product) return;

        setCart(prev => {
            const existing = prev.find(item => item.id === (product.id || product._id || product.skuid));
            if (existing) {
                return prev.map(item =>
                    item.id === existing.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            return [...prev, {
                id: product.id || product._id || product.skuid || Math.random().toString(),
                sku: product.sku || product.skuid || "",
                name: product.name || product.productName || "Unknown Product",
                productName: product.name || product.productName,
                companyid: product?.company_id,
                company: product.company || "Ayur-AI",
                price: parseFloat(product.discount_price || product.price || 0),
                actualprice: parseFloat(product.price || 0),
                quantity: 1,
                imgurl: product.imgurl || product.image || product.src || "",
                src: product.image || product.src || "",
            }];
        });
    };

    const handleUpdateQuantity = (id: string, delta: number) => {
        setCart(prev => prev.map(item => {
            if (item.id === id) {
                const newQ = Math.max(1, item.quantity + delta);
                return { ...item, quantity: newQ };
            }
            return item;
        }));
    };

    const handleRemoveProduct = (id: string) => {
        setCart(prev => prev.filter(item => item.id !== id));
    };

    const calculateTotal = () => {
        const sub = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        return Math.max(0, sub - (parseFloat(discount) || 0));
    };

    const handleSubmit = async () => {
        if (cart.length === 0) {
            setError("Please add at least one product to the cart.");
            toast.error("Cart is empty");
            return;
        }

        if (!customer.billing.contact || !customer.billing.name) {
            setError("Billing name and contact are required.");
            return;
        }

        try {
            setSubmitting(true);
            setError(null);

            const payload = {
                cartitems: cart,
                discount: parseFloat(discount) || 0,
                totalAmount: calculateTotal(),
                email: customer.billing.email || "NA",
                phone: customer.billing.contact,
                customerDetails: customer,
                date: orderDate ? format(orderDate, 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy'),
            };

            const response = await shopModel.post('/api/v1/offline-order', payload);

            toast.success('Offline order created successfully!');
            // Navigate back to orders list
            navigate('/orders');

        } catch (err: any) {
            console.error("Order submission failed:", err);
            const errMsg = err.response?.data?.message || err.message || "Failed to create offline order";
            setError(errMsg);
            toast.error(errMsg);
        } finally {
            setSubmitting(false);
        }
    };

    // UI Components
    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={() => navigate('/orders')} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: "#3B362A" }}>
                    Create Offline Order
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={4}>
                {/* Left Column: Customer Details & Product Selection */}
                <Grid item xs={12} md={7}>

                    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                                Customer Details
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Typography variant="subtitle2" sx={{ mb: 1.5, color: "text.secondary", fontWeight: 600 }}>Order Date</Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="Select Order Date"
                                            value={orderDate}
                                            onChange={(newValue) => setOrderDate(newValue)}
                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle2" sx={{ mb: 1.5, color: "text.secondary" }}>Billing Info</Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Full Name *"
                                        size="small"
                                        value={customer.billing.name}
                                        onChange={(e) => handleCustomerChange('billing', 'name', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone Number *"
                                        size="small"
                                        value={customer.billing.contact}
                                        onChange={(e) => handleCustomerChange('billing', 'contact', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        size="small"
                                        type="email"
                                        value={customer.billing.email}
                                        onChange={(e) => handleCustomerChange('billing', 'email', e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>Shipping Details</Typography>
                                <Button
                                    size="small"
                                    sx={{ textTransform: 'none' }}
                                    onClick={() => setUseBillingForShipping(!useBillingForShipping)}
                                >
                                    {useBillingForShipping ? "Unlink from Billing" : "Link with Billing Name/Phone"}
                                </Button>
                            </Box>

                            <Grid container spacing={2}>
                                {!useBillingForShipping && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Recipient Name"
                                                size="small"
                                                value={customer.shipping.name}
                                                onChange={(e) => handleCustomerChange('shipping', 'name', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Recipient Phone"
                                                size="small"
                                                value={customer.shipping.phone}
                                                onChange={(e) => handleCustomerChange('shipping', 'phone', e.target.value)}
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Address Line 1"
                                        size="small"
                                        value={customer.shipping.addressLine1}
                                        onChange={(e) => handleCustomerChange('shipping', 'addressLine1', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="City"
                                        size="small"
                                        value={customer.shipping.city}
                                        onChange={(e) => handleCustomerChange('shipping', 'city', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="State"
                                        size="small"
                                        value={customer.shipping.state}
                                        onChange={(e) => handleCustomerChange('shipping', 'state', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Postal Code"
                                        size="small"
                                        value={customer.shipping.postalCode}
                                        onChange={(e) => handleCustomerChange('shipping', 'postalCode', e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                        </CardContent>
                    </Card>

                    <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                                Add Products
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            {/* Search Autocomplete - fixed with controlled inputValue */}
                            <Autocomplete
                                fullWidth
                                options={searchSuggestions}
                                getOptionLabel={(option) => option.name || option.productName || 'Unknown Product'}
                                inputValue={autocompleteInputValue}
                                onInputChange={(event, newInputValue, reason) => {
                                    // Only update on user input, not on blur/reset so text is preserved
                                    if (reason === 'input') {
                                        setAutocompleteInputValue(newInputValue);
                                        setSearchTerm(newInputValue);
                                    }
                                }}
                                renderOption={(props, option) => {
                                    const imagesArray = Array.isArray(option.images) ? option.images : [];
                                    const firstImageFromArray = imagesArray.length > 0
                                        ? (typeof imagesArray[0] === 'string' ? imagesArray[0] : (imagesArray[0] as any)?.src)
                                        : undefined;
                                    const image = option.imgurl || option.product_image_url || option.image || option.src || firstImageFromArray;
                                    const price = option.discount_price || option.price;
                                    return (
                                        <li {...props} key={option.id || option._id || option.skuid}>
                                            <Box display="flex" alignItems="center" gap={2} width="100%">
                                                {image ? (
                                                    <Box component="img" src={image} alt=""
                                                        sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 1, bgcolor: '#f5f5f5' }}
                                                    />
                                                ) : (
                                                    <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Typography variant="caption" color="text.secondary">Img</Typography>
                                                    </Box>
                                                )}
                                                <Box flexGrow={1}>
                                                    <Typography variant="body2" fontWeight={500}>{option.name || option.productName}</Typography>
                                                    {option.company && (
                                                        <Typography variant="caption" color="text.secondary" display="block">{option.company}</Typography>
                                                    )}
                                                </Box>
                                                <Typography variant="body2" fontWeight={600} color="primary">₹{price}</Typography>
                                            </Box>
                                        </li>
                                    );
                                }}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        handleAddProduct(newValue);
                                        // Clear search after selection
                                        setAutocompleteInputValue('');
                                        setSearchTerm('');
                                        setSearchSuggestions([]);
                                    }
                                }}
                                loading={loading}
                                filterOptions={(x) => x}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Products"
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                            />

                            {/* Pre-loaded Test Kits */}
                            <Box mt={3}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 600 }}>
                                    Test Kits
                                    {testKitsLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
                                </Typography>
                                {testKitsLoading ? (
                                    <Box display="flex" justifyContent="center" py={2}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : testKits.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">No test kits found.</Typography>
                                ) : (
                                    <Grid container spacing={1.5}>
                                        {testKits.map((kit) => {
                                            const kitId = kit.id || kit._id || kit.skuid;
                                            const kitName = kit.name || kit.productName || 'Test Kit';
                                            const kitPrice = kit.discount_price || kit.price || 0;
                                            const imagesArray = Array.isArray(kit.images) ? kit.images : [];
                                            const firstImg = imagesArray.length > 0
                                                ? (typeof imagesArray[0] === 'string' ? imagesArray[0] : (imagesArray[0] as any)?.src)
                                                : undefined;
                                            const kitImage = kit.imgurl || kit.product_image_url || kit.image || kit.src || firstImg;
                                            const isInCart = cart.some(item => item.id === kitId);
                                            return (
                                                <Grid item xs={12} sm={6} key={kitId}>
                                                    <Box
                                                        onClick={() => handleAddProduct(kit)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            p: 1.5,
                                                            border: '1px solid',
                                                            borderColor: isInCart ? 'primary.main' : '#e0e0e0',
                                                            borderRadius: 2,
                                                            cursor: 'pointer',
                                                            bgcolor: isInCart ? 'primary.50' : 'background.paper',
                                                            transition: 'all 0.15s ease',
                                                            '&:hover': {
                                                                borderColor: 'primary.main',
                                                                bgcolor: '#f5f9ff',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                            },
                                                        }}
                                                    >
                                                        {kitImage ? (
                                                            <Box component="img" src={kitImage} alt={kitName}
                                                                sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1, bgcolor: '#f5f5f5', flexShrink: 0 }}
                                                            />
                                                        ) : (
                                                            <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                <Typography variant="caption" color="text.secondary">Kit</Typography>
                                                            </Box>
                                                        )}
                                                        <Box flexGrow={1} minWidth={0}>
                                                            <Typography variant="body2" fontWeight={500} noWrap>{kitName}</Typography>
                                                            <Typography variant="caption" color="primary.main" fontWeight={600}>₹{kitPrice}</Typography>
                                                        </Box>
                                                        <IconButton
                                                            size="small"
                                                            color={isInCart ? 'success' : 'primary'}
                                                            sx={{ flexShrink: 0, bgcolor: isInCart ? 'success.50' : 'transparent' }}
                                                        >
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column: Order Summary / Cart */}
                <Grid item xs={12} md={5}>
                    <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'sticky', top: 20 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                                Order Summary
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {cart.length === 0 ? (
                                <Box py={4} textAlign="center">
                                    <Typography color="text.secondary">No items in the cart yet.</Typography>
                                </Box>
                            ) : (
                                <Table size="small" sx={{ mb: 3 }}>
                                    <TableBody>
                                        {cart.map((item) => (
                                            <TableRow key={item.id} sx={{ '& td, & th': { borderBottom: '1px solid #f0f0f0' } }}>
                                                <TableCell>
                                                    <img src={item?.imgurl} alt={item.name} style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                                                </TableCell>
                                                <TableCell sx={{ pl: 0, py: 2 }}>
                                                    <Typography variant="body2" fontWeight="500">{item.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">₹{item.price} each</Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '100px', py: 2 }}>
                                                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                        <IconButton size="small" onClick={() => handleUpdateQuantity(item.id, -1)} disabled={item.quantity <= 1}><RemoveIcon fontSize="small" /></IconButton>
                                                        <Typography variant="body2">{item.quantity}</Typography>
                                                        <IconButton size="small" onClick={() => handleUpdateQuantity(item.id, 1)}><AddIcon fontSize="small" /></IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ pr: 0, py: 2, fontWeight: 600 }}>
                                                    ₹{item.price * item.quantity}
                                                </TableCell>
                                                <TableCell align="right" sx={{ pr: 0, width: '40px' }}>
                                                    <IconButton color="error" size="small" onClick={() => handleRemoveProduct(item.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}

                            <Box sx={{ bgcolor: '#f8f8f4', p: 2, borderRadius: 2 }}>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography color="text.secondary">Subtotal</Typography>
                                    <Typography>₹{cart.reduce((sum, item) => sum + (item.price * item.quantity), 0)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography color="text.secondary">Discount (₹)</Typography>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={discount}
                                        onChange={(e) => setDiscount(e.target.value)}
                                        sx={{ width: '100px', bgcolor: 'white' }}
                                        inputProps={{ min: 0 }}
                                    />
                                </Box>
                                <Divider sx={{ my: 1.5 }} />
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="h6" fontWeight="bold">Total Amount</Typography>
                                    <Typography variant="h6" fontWeight="bold" color="primary">₹{calculateTotal()}</Typography>
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                sx={{ mt: 3, py: 1.5, fontWeight: 600, display: 'flex', gap: 1, bgcolor: "#3B362A", "&:hover": { bgcolor: "#2a2620" } }}
                                onClick={handleSubmit}
                                disabled={submitting || cart.length === 0}
                            >
                                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Confirm & Place Order'}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

            </Grid>
        </Container>
    );
};

export default CreateOfflineOrderPage;
